<template>
  <div class="container contact-section" data-aos="fade-up">
    <div class="section-title">
      <h2>{{ $t("contact.title") }}</h2>
      <p>{{ $t("contact.desc") }}</p>
    </div>

    <div class="row">
      <!-- 左側資訊 -->
      <div class="col-lg-5 d-flex align-items-stretch">
        <div class="info contact-info-panel">
          <div class="contact-summary">
            <div class="contact-item">
              <div class="contact-icon"><i class="bi bi-envelope"></i></div>
              <div class="contact-copy">
                <span class="contact-label">{{ $t("contact.email") }}</span>
                <a href="mailto:cs@union.com.tw">cs@union.com.tw</a>
              </div>
            </div>

            <div class="contact-item">
              <div class="contact-icon"><i class="bi bi-phone"></i></div>
              <div class="contact-copy">
                <span class="contact-label">{{ $t("contact.phone") }}</span>
                <div class="contact-value">
                  <a href="tel:+886-4-23292669">04-2329-2669</a>
                  <span class="contact-separator">／</span>
                  <a href="tel:+886-4-23292651">04-2329-2651</a>
                </div>
              </div>
            </div>

            <div class="contact-item">
              <div class="contact-icon"><i class="bi bi-geo-alt"></i></div>
              <div class="contact-copy">
                <span class="contact-label">{{ $t("contact.address") }}</span>
                <a
                  href="https://www.google.com/maps/place/%E8%81%AF%E6%B4%8B%E6%96%87%E5%8C%96%E4%BA%8B%E6%A5%AD%E6%9C%89%E9%99%90%E5%85%AC%E5%8F%B8+Union+Ocean"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  40861 台中市南屯區大墩十一街156號6F
                </a>
              </div>
            </div>
          </div>

          <div class="map-wrap">
            <iframe
              src="https://www.google.com/maps?q=24.1489614,120.6510528&z=16&output=embed"
              width="100%"
              class="contact-map"
              style="border: 0"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              title="Union Ocean Map"
            ></iframe>
          </div>
        </div>
      </div>

      <!-- 右側詢價表單 -->
      <div class="col-lg-7 mt-3 mt-lg-0">
        <form class="inquiry-form" @submit.prevent="submitInquiry">
          <div class="form-heading">
            <h3>書籍詢價</h3>
            <p>我們將於收到表單後，以 Email 提供完整報價資訊及聯洋文化匯款資訊。
如欲訂購，請於完成匯款後直接回覆報價信件，我們將為您安排訂書及出貨事宜，謝謝您！</p>
          </div>

          <!-- 進度 -->
          <div class="step-progress" aria-label="詢價表單進度">
            <div
              v-for="step in totalSteps"
              :key="step"
              :class="[
                'step-dot-wrap',
                { active: currentStep === step, done: currentStep > step },
              ]"
            >
              <div class="step-dot">{{ currentStep > step ? "✓" : step }}</div>
              <span>{{ stepLabels[step - 1] }}</span>
            </div>
          </div>

          <div class="slider-window">
            <div class="slider-track" :style="sliderStyle">
              <!-- Step 1 -->
              <section class="form-step">
                <div class="step-title">聯絡資料</div>
                <div class="form-grid">
                  <div class="field full">
                    <label for="name">訂購人姓名 <span>*</span></label>
                    <input
                      id="name"
                      ref="name"
                      v-model.trim="form.name"
                      type="text"
                      required
                      autocomplete="name"
                    />
                  </div>

                  <div class="field full">
                    <label for="phone">連絡電話 <span>*</span></label>
                    <input
                      id="phone"
                      ref="phone"
                      v-model.trim="form.phone"
                      type="tel"
                      required
                      autocomplete="tel"
                    />
                  </div>

                  <div class="field full">
                    <label for="email">電子郵件地址 <span>*</span></label>
                    <input
                      id="email"
                      ref="email"
                      v-model.trim="form.email"
                      type="email"
                      required
                      autocomplete="email"
                    />
                  </div>
                </div>
              </section>

              <!-- Step 2 -->
              <section class="form-step">
                <div class="step-title">配送&發票</div>
                <div class="form-grid">
                  <div class="field full">
                    <label for="address">送貨地址 <span>*</span></label>
                    <input
                      id="address"
                      ref="address"
                      v-model.trim="form.address"
                      type="text"
                      required
                      autocomplete="street-address"
                    />
                  </div>

                  <div class="field full">
                    <label>需不需要抬頭統編 <span>*</span></label>
                    <div class="radio-group radio-group-centered">
                      <label class="radio-option">
                        <input
                          v-model="invoiceType"
                          type="radio"
                          value="不需要"
                        />
                        <span>不需要</span>
                      </label>
                      <label class="radio-option">
                        <input
                          v-model="invoiceType"
                          type="radio"
                          value="需要"
                        />
                        <span>需要</span>
                      </label>
                    </div>
                  </div>

                  <div
                    v-if="invoiceType === '需要'"
                    class="field full invoice-detail"
                  >
                    <label for="invoiceDetail">抬頭／統編 <span>*</span></label>
                    <input
                      id="invoiceDetail"
                      ref="invoiceDetail"
                      v-model.trim="invoiceDetail"
                      type="text"
                      placeholder="請填寫抬頭／統編"
                    />
                  </div>
                </div>
              </section>

              <!-- Step 3 -->
              <section class="form-step">
                <div class="step-title">詢價內容</div>
                <div class="form-grid">
                  <div class="field full">
                    <label for="books"
                      >書籍名稱 / ISBN / 數量 <span>*</span></label
                    >
                    <textarea
                      id="books"
                      ref="books"
                      v-model.trim="form.books"
                      rows="4"
                      required
                      placeholder="例如：Into Reading Grade 3／ISBN 978xxxx／2 本"
                    ></textarea>
                  </div>

                  <div class="field full">
                    <label for="note">備註</label>
                    <textarea
                      id="note"
                      v-model.trim="form.note"
                      rows="3"
                      placeholder="若有其他需求可填寫於此"
                    ></textarea>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div v-if="statusMessage" :class="['status-message', statusType]">
            {{ statusMessage }}
          </div>

          <div class="step-actions">
            <button
              v-if="currentStep > 1"
              class="step-btn secondary"
              type="button"
              :disabled="submitting"
              @click="prevStep"
            >
              上一步
            </button>

            <button
              v-if="currentStep < totalSteps"
              class="step-btn primary"
              type="button"
              @click="nextStep"
            >
              下一步
            </button>

            <button
              v-else
              class="step-btn primary"
              type="submit"
              :disabled="submitting"
            >
              {{ submitting ? "送出中..." : "送出詢價" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
const APPS_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzm6LvpiLJd1FN6G-PyUN8wMbQqy8bQUQ-cZTAkz-0jfSR-eakroRiku6MOubpGVOtGMQ/exec";

export default {
  name: "ContactMe",
  data() {
    return {
      currentStep: 1,
      totalSteps: 3,
      stepLabels: ["聯絡資料", "配送&發票", "詢價內容"],
      submitting: false,
      statusMessage: "",
      statusType: "",
      invoiceType: "不需要",
      invoiceDetail: "",
      form: {
        email: "",
        name: "",
        phone: "",
        address: "",
        books: "",
        note: "",
      },
    };
  },
  computed: {
    sliderStyle() {
      return {
        transform: `translateX(-${(this.currentStep - 1) * 100}%)`,
      };
    },
  },
  methods: {
    focusField(refName) {
      this.$nextTick(() => {
        const field = this.$refs[refName];
        if (field && typeof field.focus === "function") field.focus();
      });
    },
    validateStep() {
      this.statusMessage = "";
      this.statusType = "";

      if (this.currentStep === 1) {
        if (!this.form.name) {
          this.focusField("name");
          return false;
        }
        if (!this.form.phone) {
          this.focusField("phone");
          return false;
        }
        if (!this.form.email) {
          this.focusField("email");
          return false;
        }
        const emailEl = this.$refs.email;
        if (emailEl && !emailEl.checkValidity()) {
          emailEl.reportValidity();
          return false;
        }
      }

      if (this.currentStep === 2) {
        if (!this.form.address) {
          this.focusField("address");
          return false;
        }
        if (this.invoiceType === "需要" && !this.invoiceDetail) {
          this.focusField("invoiceDetail");
          return false;
        }
      }

      if (this.currentStep === 3) {
        if (!this.form.books) {
          this.focusField("books");
          return false;
        }
      }

      return true;
    },
    nextStep() {
      if (!this.validateStep()) return;
      if (this.currentStep < this.totalSteps) this.currentStep += 1;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
      this.statusMessage = "";
    },
    async submitInquiry() {
      if (!this.validateStep()) return;

      this.submitting = true;
      this.statusMessage = "";
      this.statusType = "";

      const invoice =
        this.invoiceType === "需要"
          ? `需要${this.invoiceDetail ? `：${this.invoiceDetail}` : ""}`
          : "不需要";

      const body = new URLSearchParams({
        email: this.form.email,
        name: this.form.name,
        phone: this.form.phone,
        address: this.form.address,
        invoice,
        books: this.form.books,
        note: this.form.note,
        photo: "",
      });

      try {
        await fetch(APPS_SCRIPT_URL, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          },
          body,
        });

        this.statusType = "success";
        this.statusMessage = "詢價資料已送出，謝謝您！";

        this.form = {
          email: "",
          name: "",
          phone: "",
          address: "",
          books: "",
          note: "",
        };
        this.invoiceType = "不需要";
        this.invoiceDetail = "";
        this.currentStep = 1;
      } catch (error) {
        console.error("Inquiry submit error:", error);
        this.statusType = "error";
        this.statusMessage = "送出時發生問題，請稍後再試，或直接來電聯絡我們。";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* 聯絡我們：整區使用淡灰藍底，並與前後區塊保留呼吸空間 */
.contact-section {
  position: relative;
  z-index: 0;
  padding-top: 70px;
  padding-bottom: 80px;
  margin-bottom: 80px;
}

/* 背景延伸到整個螢幕寬度，不只 container */
.contact-section::before {
  content: "";
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  background: #f3f5fa;
}

.contact-info-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfd 100%);
  border: 1px solid #dfe7ee;
  border-top: 3px solid #45aee0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(31, 69, 103, 0.08);
}

.contact-summary {
  display: grid;
  gap: 12px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e4edf3;
  border-radius: 10px;
}

.contact-icon {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #eaf7fd;
  color: #36a9df;
  font-size: 19px;
}

.contact-info-panel .contact-icon i {
  float: none !important;
  width: auto !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  color: inherit !important;
  font-size: inherit !important;
  line-height: 1 !important;
}

.contact-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  text-align: center;
}

.contact-label {
  color: #274d73;
  font-size: 15px;
  font-weight: 700;
}

.contact-copy a,
.contact-value {
  color: #45657d;
  font-size: 14px;
  line-height: 1.55;
  text-decoration: none;
  text-align: center;
  word-break: break-word;
}

.contact-value {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.contact-copy a:hover {
  color: #2f8fbd;
}

.contact-separator {
  color: #91a5b4;
}

.map-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #dfe7ee;
  background: #eef5f9;
}
.contact-map {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 330px;
}

.inquiry-form {
  height: 100%;
  padding: 30px;
  background: #fff;
  border: 1px solid #dfe7ee;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(31, 69, 103, 0.08);
}
.form-heading {
  margin-bottom: 20px;
  text-align: center;
}
.form-heading h3 {
  margin: 0 0 8px;
  color: #274d73;
  font-size: 26px;
  font-weight: 700;
}
.form-heading p {
  margin: 0;
  color: #6b7c89;
  line-height: 1.7;
}

.step-progress {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 4px 0 26px;
}
.step-dot-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #91a5b4;
  font-size: 12px;
  font-weight: 600;
}
.step-dot-wrap:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 15px;
  left: calc(50% + 20px);
  width: calc(100% - 40px);
  height: 2px;
  background: #dfe7ee;
}
.step-dot {
  position: relative;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: 2px solid #d7e2ea;
  border-radius: 50%;
  background: #fff;
  color: #8298a7;
}
.step-dot-wrap.active,
.step-dot-wrap.done {
  color: #2f78a8;
}
.step-dot-wrap.active .step-dot,
.step-dot-wrap.done .step-dot {
  border-color: #45aee0;
  background: #eaf7fd;
  color: #2f78a8;
}
.step-dot-wrap.done:not(:last-child)::after {
  background: #8dcbea;
}

.slider-window {
  width: 100%;
  overflow: hidden;
}
.slider-track {
  display: flex;
  width: 100%;
  transition: transform 0.35s ease;
  will-change: transform;
}
.form-step {
  flex: 0 0 100%;
  width: 100%;
  padding: 2px 2px 6px;
}
.step-title {
  margin-bottom: 18px;
  color: #274d73;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field.full {
  grid-column: 1 / -1;
}
.field label {
  color: #324d62;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
}
.field label span {
  color: #d9534f;
}
.field input,
.field textarea {
  width: 100%;
  border: 1px solid #cfdbe4;
  border-radius: 8px;
  padding: 11px 13px;
  color: #2f4555;
  background: #fff;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.field textarea {
  resize: vertical;
  min-height: 86px;
}
.field input:focus,
.field textarea:focus {
  border-color: #45aee0;
  box-shadow: 0 0 0 3px rgba(69, 174, 224, 0.14);
}
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 26px;
  padding-top: 2px;
}
.radio-group-centered {
  justify-content: center;
}
.radio-option {
  display: inline-flex !important;
  align-items: center;
  gap: 7px;
  margin: 0;
  cursor: pointer;
  color: #324d62 !important;
}
.radio-option span {
  color: #324d62 !important;
}
.radio-option input {
  width: auto;
  margin: 0;
}
.invoice-detail {
  margin-top: 2px;
}

.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
.step-btn {
  min-width: 130px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.step-btn.primary {
  border: 0;
  background: #2f78a8;
  color: #fff;
}
.step-btn.secondary {
  border: 1px solid #cbd9e3;
  background: #fff;
  color: #4a687c;
}
.step-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  opacity: 0.92;
}
.step-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status-message {
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
}
.status-message.success {
  background: #eef8f1;
  color: #2d6a43;
  border: 1px solid #cfe8d7;
}
.status-message.error {
  background: #fff1f1;
  color: #9b3c3c;
  border: 1px solid #f1cccc;
}

@media (max-width: 991px) {
  .contact-section {
    padding-top: 55px;
    padding-bottom: 60px;
    margin-bottom: 60px;
  }
  .contact-info-panel {
    height: auto;
  }
  .map-wrap {
    flex: none;
  }
  .contact-map {
    min-height: 300px;
    height: 300px;
  }
  .inquiry-form {
    margin-top: 10px;
  }
}

@media (max-width: 767px) {
  .contact-info-panel {
    padding: 18px;
  }
  .contact-item {
    align-items: flex-start;
    padding: 13px 14px;
  }
  .contact-value {
    display: flex;
    flex-wrap: wrap;
  }
  .inquiry-form {
    padding: 22px 18px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .field.full {
    grid-column: auto;
  }
  .step-progress {
    gap: 4px;
  }
  .step-dot-wrap {
    font-size: 11px;
  }
  .step-dot-wrap:not(:last-child)::after {
    left: calc(50% + 17px);
    width: calc(100% - 34px);
  }
  .step-actions {
    justify-content: stretch;
  }
  .step-btn {
    flex: 1;
    min-width: 0;
  }
}
</style>
